import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../api";
import { PagedList } from "../../components/PagedList";
import { Alert, Card } from "../../shell/ui";

interface FeedbackRow {
  id: number;
  kind: "feedback" | "bug" | "idea";
  message: string;
  context: { route?: string; layout?: string; userAgent?: string };
  status: "open" | "done" | "ignored";
  created_at: string;
  username: string | null;
}

/** What the beta users sent through the feedback button. */
export function FeedbackTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "feedback"],
    queryFn: () => api<{ items: FeedbackRow[] }>("/admin/feedback"),
    refetchInterval: 30_000,
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: number; status: FeedbackRow["status"] }) =>
      api(`/admin/feedback/${v.id}`, { method: "PATCH", body: { status: v.status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "feedback"] }),
  });
  if (q.error) return <Alert tone="error">{q.error.message}</Alert>;
  const items = q.data?.items ?? [];
  return (
    <Card title={`Feedback (${items.filter((i) => i.status === "open").length} open)`} flush>
      {items.length === 0 ? (
        <p className="muted">Nothing yet.</p>
      ) : (
        <PagedList
          className="stack"
          items={items}
          render={(f) => (
            <li key={f.id} className="card__body">
              <div className="row">
                <strong>{f.kind}</strong>
                <span className="muted small">
                  {f.username ?? "deleted user"} · {new Date(f.created_at).toLocaleString()} ·{" "}
                  {f.context.route ?? ""}
                </span>
                <select
                  value={f.status}
                  onChange={(e) =>
                    setStatus.mutate({ id: f.id, status: e.target.value as FeedbackRow["status"] })
                  }
                  aria-label="Status"
                >
                  <option value="open">open</option>
                  <option value="done">done</option>
                  <option value="ignored">ignored</option>
                </select>
              </div>
              <p>{f.message}</p>
            </li>
          )}
        />
      )}
    </Card>
  );
}
